/**
 * Format a raw address string into proper title case with state abbreviation and commas.
 * Example: "786 shaz lane Houston texas 77479" → "786 Shaz Lane, Houston, TX 77479"
 */

const STATE_ABBR = {
  'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR',
  'california': 'CA', 'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE',
  'florida': 'FL', 'georgia': 'GA', 'hawaii': 'HI', 'idaho': 'ID',
  'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA', 'kansas': 'KS',
  'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
  'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS',
  'missouri': 'MO', 'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV',
  'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY',
  'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH', 'oklahoma': 'OK',
  'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
  'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT',
  'vermont': 'VT', 'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV',
  'wisconsin': 'WI', 'wyoming': 'WY',
};

const STREET_SUFFIXES = /\b(Street|St|Avenue|Ave|Boulevard|Blvd|Drive|Dr|Lane|Ln|Road|Rd|Court|Ct|Way|Place|Pl|Circle|Cir|Parkway|Pkwy|Highway|Hwy|Loop|Trail|Trl)\b/i;

export function formatAddress(addr) {
  if (!addr || typeof addr !== 'string') return addr || '';

  let result = addr;

  // Replace full state names with abbreviations (case-insensitive)
  for (const [name, abbr] of Object.entries(STATE_ABBR)) {
    result = result.replace(new RegExp(`\\b${name}\\b`, 'gi'), abbr);
  }

  // Title case each word, preserving state codes and zip codes
  result = result.replace(/\b[a-zA-Z]+/g, (w) => {
    if (/^[A-Z]{2}$/.test(w)) return w;                    // Already state code
    return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
  });

  // If no commas exist, try to insert them smartly
  if (!result.includes(',')) {
    const suffixMatch = result.match(STREET_SUFFIXES);
    if (suffixMatch) {
      const idx = result.indexOf(suffixMatch[0]) + suffixMatch[0].length;
      const street = result.substring(0, idx);
      const rest = result.substring(idx).trim();
      if (rest) {
        // Insert comma after street suffix, and before state code
        const stateMatch = rest.match(/\s([A-Z]{2})\s*(\d{5})?/);
        if (stateMatch) {
          const stateIdx = rest.indexOf(stateMatch[0]);
          const city = rest.substring(0, stateIdx).trim();
          const stateZip = rest.substring(stateIdx).trim();
          result = city ? `${street}, ${city}, ${stateZip}` : `${street}, ${stateZip}`;
        } else {
          result = `${street}, ${rest}`;
        }
      }
    }
  }

  return result.replace(/\s+/g, ' ').trim();
}

/**
 * Estimated risk tier based on FICO when real risk score is unavailable.
 */
export function estimatedRiskTier(fico) {
  const score = parseInt(fico);
  if (!score || isNaN(score)) return null;
  if (score >= 740) return { label: "Low Risk (est.)", color: "text-emerald-600", bg: "bg-emerald-50" };
  if (score >= 700) return { label: "Low-Medium (est.)", color: "text-teal-600", bg: "bg-teal-50" };
  if (score >= 680) return { label: "Medium Risk (est.)", color: "text-amber-600", bg: "bg-amber-50" };
  return { label: "Higher Risk (est.)", color: "text-red-500", bg: "bg-red-50" };
}

/**
 * Extract just "City, ST" from a full address string.
 * Example: "786 Shaz Lane, Houston, TX 77479" → "Houston, TX"
 */
export function cityStateOnly(addr) {
  if (!addr || typeof addr !== 'string') return '';
  const formatted = formatAddress(addr);
  // Try to match ", City, ST ZIP" or ", City, ST" pattern
  const parts = formatted.split(',').map(p => p.trim());
  if (parts.length >= 3) {
    // "Street, City, ST ZIP" → "City, ST"
    const stateZip = parts[parts.length - 1]; // "TX 77479"
    const city = parts[parts.length - 2]; // "Houston"
    const stateOnly = stateZip.replace(/\s*\d{5}(-\d{4})?$/, '').trim();
    return `${city}, ${stateOnly}`;
  }
  if (parts.length === 2) {
    const stateOnly = parts[1].replace(/\s*\d{5}(-\d{4})?$/, '').trim();
    return stateOnly;
  }
  return formatted;
}

/**
 * Display value for nullable fields — replaces dashes, nulls, empties with "Not provided".
 */
export function displayValue(val) {
  if (val === null || val === undefined || val === '' || val === '-' || val === 'null' || val === 'N/A' || val === 'n/a') {
    return null; // Caller should render "Not provided" in muted style
  }
  return val;
}
